import * as yup from 'yup';

import {
  EmailLogoTypesEnum,
  IOrganizationCustomizationOptions,
  IOrganizationFormValues,
  IRunAnonymizationCheckState,
  LastRunViewEnum,
  OrganizationFilterTypesEnum,
  RunAnonymizationCheckPhaseEnum,
  SigninMethodsEnum,
} from '../types/organizations';
import { isCadenceUnsetByBackend } from '../utils/organizations';
import { BLUEBIRD, crmSupports, emailSupports, HUBSPOT_APP, NONE } from './integrations';
import { URL_VARS } from './routing';
import {
  CUSTOMIZE_ITEMS_PAGE_DEFAULT_BODY,
  CUSTOMIZE_ITEMS_PAGE_DEFAULT_TITLE,
  EMAIL_CUSTOM_SENDER_DEFAULT,
  EMAIL_DEFAULT_ACCENT_COLOR,
  EMAIL_DEFAULT_LOGO_TYPE,
  EMAIL_DEFAULT_MAIN_COLOR,
  EMAIL_DEFAULT_USE_ORG_LOGO,
  EMAIL_VALIDATION_REGEX,
  MSKU_PAGE_DEFAULT_BODY,
  MSKU_PAGE_DEFAULT_TITLE,
  PICK_YOUR_GIFT_DEFAULT_BODY,
  PICK_YOUR_GIFT_DEFAULT_TITLE,
  REQUIRED_FIELD_MESSAGE,
  SHIPPING_PAGE_DEFAULT_BODY,
  SHIPPING_PAGE_DEFAULT_BUTTON_COLOR,
  SHIPPING_PAGE_DEFAULT_HEADER_COLOR,
  SHIPPING_PAGE_DEFAULT_TITLE,
  SQUARE_BRACKETS_EXTRACTOR_REGEX,
  THEME_DEFAULT_ACCENT_COLOR,
  THEME_DEFAULT_SIDEBAR_ACCENT_COLOR,
  THEME_DEFAULT_SIDEBAR_COLOR,
  THEME_DEFAULT_SIDEBAR_TEXT_COLOR,
  THEME_DEFAULT_USE_COBRANDING_ORG_LOGO,
  THEME_DEFAULT_USE_ORG_LOGO,
} from './shell';

import {
  AI_GIFT_SUGGESTION_ENABLED,
  AUTOMATIONS_ENABLED,
  AUTOMATION_CONNECTIONS,
  AVAILABLE_BUDGET,
  BUTTON_COLOR,
  CITY,
  COUNTRY,
  CRM_CURRENTLY_SUPPORTS,
  CRM_SKIP_ALLOWED,
  CUSTOMIZE_ITEMS_PAGE_BODY,
  CUSTOMIZE_ITEMS_PAGE_TITLE,
  DELAYED_SHIPPING_BASE_URL,
  DELAYED_SHIPPING_PAGE,
  DEPARTMENT_BUDGET_ENABLED,
  DIGITAL_BALANCE_LIMIT_ENABLED,
  DIGITAL_BALANCE_LIMIT_VALUE,
  EMAIL,
  EMAIL_ACCENT_COLOR,
  EMAIL_CURRENTLY_SUPPORTS,
  EMAIL_CUSTOM_SENDER,
  EMAIL_LOGO_TYPE,
  EMAIL_MAIN_COLOR,
  EMAIL_USE_ORG_LOGO,
  HEADER_COLOR,
  HUBSPOT_APP_ENABLED,
  LOGO_URL,
  LOW_INVENTORY_NOTIFICATION_EMAILS,
  MSKU_PAGE_BODY,
  MSKU_PAGE_TITLE,
  NAME,
  ORDER_KEY,
  ORG_CUSTOMIZATION_OPTIONS,
  ORG_OPTIONS,
  PHONE,
  PICK_YOUR_GIFT_BODY,
  PICK_YOUR_GIFT_TITLE,
  PORTAL_ACCENT_COLOR,
  PORTAL_SIDEBAR_ACCENT_COLOR,
  PORTAL_SIDEBAR_COLOR,
  PORTAL_SIDEBAR_TEXT_COLOR,
  PORTAL_USE_COBRANDING_LOGO,
  PORTAL_USE_ORG_LOGO,
  POSTAL_CODE,
  PRODUCT_MARKETPLACE_ENABLED,
  RECT_COBRANDING_LOGO_URL,
  RECURRING_ANONYMIZATION,
  RECURRING_ANONYMIZATION_CADENCE_DAYS,
  RECURRING_ANONYMIZATION_CADENCE_INTEGER_MESSAGE,
  RECURRING_ANONYMIZATION_CADENCE_MIN_MESSAGE,
  RECURRING_ANONYMIZATION_CADENCE_REQUIRED_MESSAGE,
  RECURRING_ANONYMIZATION_DEFAULT_CADENCE_DAYS,
  RECURRING_ANONYMIZATION_ENABLED,
  RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS,
  RESIDENTIAL,
  RYBBON_CLIENT_ID,
  RYBBON_CLIENT_SECRET,
  SALESFORCE_CAMPAIGN_ID,
  SALESFORCE_CLIENT_ID,
  SALESFORCE_CLIENT_SECRET,
  SHIPPING_ADDRESS,
  SHIPPING_PAGE_BODY,
  SHIPPING_PAGE_TITLE,
  SIGN_IN_METHOD_SUPPORTS,
  SOB_ENABLED,
  SQUARE_COBRANDING_LOGO_URL,
  SQUARE_LOGO_URL,
  STATE,
  STREET1,
  STREET2,
  USED_BUDGET,
  USER_BUDGET_ENABLED,
  VECTOR_IMAGE_URL,
} from './organizationsKeys';

export * from './organizationsKeys';

export const RUN_CHECK_LABEL: Record<RunAnonymizationCheckPhaseEnum, string> = {
  [RunAnonymizationCheckPhaseEnum.Idle]: 'Run now',
  [RunAnonymizationCheckPhaseEnum.Starting]: 'Starting…',
  [RunAnonymizationCheckPhaseEnum.Running]: 'Running…',
};

export const LAST_RUN_VIEW_LABEL: Record<Exclude<LastRunViewEnum, LastRunViewEnum.None>, string> = {
  [LastRunViewEnum.Completed]: 'Completed',
  [LastRunViewEnum.NoMatches]: 'Completed',
  [LastRunViewEnum.Failed]: 'Failed',
};

export const IDLE_RUN_ANONYMIZATION_CHECK: IRunAnonymizationCheckState = {
  orgId: null,
  phase: RunAnonymizationCheckPhaseEnum.Idle,
};

export const ORGANIZATION_VALIDATION_SCHEMA = yup.object().shape(
  {
    [NAME]: yup.string().required(REQUIRED_FIELD_MESSAGE),
    // Only the organization name is required; contact and address details are often unknown at setup.
    [SHIPPING_ADDRESS]: yup.object().shape({
      [NAME]: yup.string().nullable(),
      [EMAIL]: yup.string().nullable().email('Email is invalid'),
      [STREET1]: yup.string().nullable(),
      [STREET2]: yup.string().nullable(),
      [CITY]: yup.string().nullable(),
      [STATE]: yup.string().nullable(),
      [POSTAL_CODE]: yup.string().nullable(),
      [COUNTRY]: yup.string().nullable(),
      [PHONE]: yup.string().nullable(),
    }),
    [ORDER_KEY]: yup
      .string()
      .min(3, 'Key is too short')
      .max(10, 'Max key length is 10 characters')
      .required(REQUIRED_FIELD_MESSAGE),
    [RYBBON_CLIENT_ID]: yup.string(),
    [RYBBON_CLIENT_SECRET]: yup.string(),
    // [BILLING_PLAN_ID]: yup.string(),
    [SALESFORCE_CAMPAIGN_ID]: yup.string(),
    [SALESFORCE_CLIENT_ID]: yup.string(),
    [SALESFORCE_CLIENT_SECRET]: yup.string(),
    // [WOOCOMMERCE_HOST_URL]: yup.string(),
    // [WOOCOMMERCE_SERVICE_ACCOUNT_USER_ID]: yup.string(),
    // [WOOCOMMERCE_API_KEY]: yup.string(),
    [DELAYED_SHIPPING_PAGE]: yup.object().shape({
      [HEADER_COLOR]: yup.string().required(REQUIRED_FIELD_MESSAGE),
      [BUTTON_COLOR]: yup.string().required(REQUIRED_FIELD_MESSAGE),
      [LOGO_URL]: yup.string().required('This logo is required'),
    }),
    [SQUARE_LOGO_URL]: yup.string(),
    [VECTOR_IMAGE_URL]: yup.string(),
    [RECT_COBRANDING_LOGO_URL]: yup.string(),
    [SQUARE_COBRANDING_LOGO_URL]: yup.string(),
    [DELAYED_SHIPPING_BASE_URL]: yup.string().required(REQUIRED_FIELD_MESSAGE),
    [ORG_OPTIONS]: yup.object().shape({
      [CRM_CURRENTLY_SUPPORTS]: yup.string().required(REQUIRED_FIELD_MESSAGE).oneOf(crmSupports),
      [EMAIL_CURRENTLY_SUPPORTS]: yup.string().required(REQUIRED_FIELD_MESSAGE).oneOf(emailSupports),
      [SIGN_IN_METHOD_SUPPORTS]: yup.string().required(REQUIRED_FIELD_MESSAGE).oneOf(Object.values(SigninMethodsEnum)),
      [CRM_SKIP_ALLOWED]: yup.boolean(),
      [SOB_ENABLED]: yup.boolean().required(),
      [USER_BUDGET_ENABLED]: yup.boolean(),
      [DEPARTMENT_BUDGET_ENABLED]: yup.boolean().required(),
      [DIGITAL_BALANCE_LIMIT_ENABLED]: yup.boolean(),
      [AUTOMATIONS_ENABLED]: yup.boolean(),
      [AI_GIFT_SUGGESTION_ENABLED]: yup.boolean(),
      [HUBSPOT_APP_ENABLED]: yup.boolean(),
      [PRODUCT_MARKETPLACE_ENABLED]: yup.boolean(),
      [DIGITAL_BALANCE_LIMIT_VALUE]: yup
        .number()
        .nullable()
        .when(DIGITAL_BALANCE_LIMIT_ENABLED, {
          is: (isBalanceEnabled: boolean) => {
            return isBalanceEnabled;
          },

          then: (sc) => sc.required(REQUIRED_FIELD_MESSAGE),
        }),
      [RECURRING_ANONYMIZATION]: yup.object().shape({
        [RECURRING_ANONYMIZATION_ENABLED]: yup.boolean(),
        [RECURRING_ANONYMIZATION_CADENCE_DAYS]: yup
          .number()
          .nullable()
          .integer(RECURRING_ANONYMIZATION_CADENCE_INTEGER_MESSAGE)
          .when(RECURRING_ANONYMIZATION_ENABLED, {
            is: true,
            then: (sc) =>
              sc
                .required(RECURRING_ANONYMIZATION_CADENCE_REQUIRED_MESSAGE)
                .min(RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS, RECURRING_ANONYMIZATION_CADENCE_MIN_MESSAGE),
            otherwise: (sc) =>
              sc.test(
                'zero-or-min-cadence',
                RECURRING_ANONYMIZATION_CADENCE_MIN_MESSAGE,
                (value) =>
                  value === null ||
                  value === undefined ||
                  isCadenceUnsetByBackend(value) ||
                  value >= RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS,
              ),
          }),
      }),
    }),
    [ORG_CUSTOMIZATION_OPTIONS]: yup.object().shape({
      [EMAIL_LOGO_TYPE]: yup.string(),
      [EMAIL_MAIN_COLOR]: yup.string(),
      [EMAIL_ACCENT_COLOR]: yup.string(),
      [EMAIL_CUSTOM_SENDER]: yup.string().email('Invalid email'),
      [EMAIL_USE_ORG_LOGO]: yup.boolean(),
      [PORTAL_ACCENT_COLOR]: yup.string(),
      [PORTAL_SIDEBAR_COLOR]: yup.string(),
      [PORTAL_SIDEBAR_TEXT_COLOR]: yup.string(),
      [PORTAL_SIDEBAR_ACCENT_COLOR]: yup.string(),
      [PORTAL_USE_ORG_LOGO]: yup.boolean(),
      [PORTAL_USE_COBRANDING_LOGO]: yup.boolean(),
      [SHIPPING_PAGE_TITLE]: yup.string(),
      [SHIPPING_PAGE_BODY]: yup.string(),
      [PICK_YOUR_GIFT_TITLE]: yup.string(),
      [PICK_YOUR_GIFT_BODY]: yup.string(),
      [MSKU_PAGE_TITLE]: yup.string(),
      [MSKU_PAGE_BODY]: yup.string(),
      [CUSTOMIZE_ITEMS_PAGE_TITLE]: yup.string(),
      [CUSTOMIZE_ITEMS_PAGE_BODY]: yup.string(),
    }),
    [LOW_INVENTORY_NOTIFICATION_EMAILS]: yup
      .array()
      .of(
        yup
          .string()
          .required()
          .test({
            name: 'is-email-pattern',
            message: ({ path = '', value }) => {
              const index = (() => {
                const match = new RegExp(SQUARE_BRACKETS_EXTRACTOR_REGEX).exec(path);

                return match ? parseInt(match[1], 10) : null;
              })();

              return {
                index,
                message: `"${value}" is not a valid email address.`,
              };
            },
            test: (value) => {
              return new RegExp(EMAIL_VALIDATION_REGEX).test(value);
            },
          }),
      )
      .nullable(),
  },
  [[DIGITAL_BALANCE_LIMIT_ENABLED, DIGITAL_BALANCE_LIMIT_VALUE]],
);

export const INITIAL_ORGANIZATION_FORM_STATE: Partial<IOrganizationFormValues> = {
  [NAME]: '',
  [SHIPPING_ADDRESS]: {
    [NAME]: '',
    [EMAIL]: '',
    [STREET1]: '',
    [STREET2]: '',
    [CITY]: '',
    [STATE]: '',
    [POSTAL_CODE]: '',
    [COUNTRY]: '',
    [PHONE]: '',
    [RESIDENTIAL]: false,
  },
  [ORDER_KEY]: '',
  [RYBBON_CLIENT_ID]: '',
  [RYBBON_CLIENT_SECRET]: '',
  [SALESFORCE_CAMPAIGN_ID]: '',
  [SALESFORCE_CLIENT_ID]: '',
  [SALESFORCE_CLIENT_SECRET]: '',
  [DELAYED_SHIPPING_PAGE]: {
    [HEADER_COLOR]: SHIPPING_PAGE_DEFAULT_HEADER_COLOR,
    [BUTTON_COLOR]: SHIPPING_PAGE_DEFAULT_BUTTON_COLOR,
    [LOGO_URL]: '',
  },
  [SQUARE_LOGO_URL]: '',
  [VECTOR_IMAGE_URL]: '',
  [SQUARE_COBRANDING_LOGO_URL]: '',
  [RECT_COBRANDING_LOGO_URL]: '',
  [ORG_OPTIONS]: {
    [CRM_CURRENTLY_SUPPORTS]: NONE,
    [CRM_SKIP_ALLOWED]: false,
    [AUTOMATION_CONNECTIONS]: [],
    [EMAIL_CURRENTLY_SUPPORTS]: BLUEBIRD,
    [SIGN_IN_METHOD_SUPPORTS]: SigninMethodsEnum.All,
    [SOB_ENABLED]: false,
    [USER_BUDGET_ENABLED]: false,
    [DEPARTMENT_BUDGET_ENABLED]: false,
    [AI_GIFT_SUGGESTION_ENABLED]: false,
    [DIGITAL_BALANCE_LIMIT_ENABLED]: false,
    [AUTOMATIONS_ENABLED]: false,
    [HUBSPOT_APP_ENABLED]: false,
    [PRODUCT_MARKETPLACE_ENABLED]: false,
    [DIGITAL_BALANCE_LIMIT_VALUE]: null,
    [RECURRING_ANONYMIZATION]: {
      [RECURRING_ANONYMIZATION_ENABLED]: false,
      [RECURRING_ANONYMIZATION_CADENCE_DAYS]: RECURRING_ANONYMIZATION_DEFAULT_CADENCE_DAYS,
    },
    [HUBSPOT_APP]: null,
  },
  [ORG_CUSTOMIZATION_OPTIONS]: {
    [EMAIL_LOGO_TYPE]: EMAIL_DEFAULT_LOGO_TYPE,
    [EMAIL_MAIN_COLOR]: EMAIL_DEFAULT_MAIN_COLOR,
    [EMAIL_ACCENT_COLOR]: EMAIL_DEFAULT_ACCENT_COLOR,
    [EMAIL_USE_ORG_LOGO]: EMAIL_DEFAULT_USE_ORG_LOGO,
    [PORTAL_ACCENT_COLOR]: THEME_DEFAULT_ACCENT_COLOR,
    [PORTAL_SIDEBAR_COLOR]: THEME_DEFAULT_SIDEBAR_COLOR,
    [PORTAL_SIDEBAR_TEXT_COLOR]: THEME_DEFAULT_SIDEBAR_TEXT_COLOR,
    [PORTAL_SIDEBAR_ACCENT_COLOR]: THEME_DEFAULT_SIDEBAR_ACCENT_COLOR,
    [PORTAL_USE_ORG_LOGO]: THEME_DEFAULT_USE_ORG_LOGO,
    [PORTAL_USE_COBRANDING_LOGO]: THEME_DEFAULT_USE_COBRANDING_ORG_LOGO,
    [SHIPPING_PAGE_TITLE]: SHIPPING_PAGE_DEFAULT_TITLE,
    [SHIPPING_PAGE_BODY]: SHIPPING_PAGE_DEFAULT_BODY,
    [PICK_YOUR_GIFT_TITLE]: PICK_YOUR_GIFT_DEFAULT_TITLE,
    [PICK_YOUR_GIFT_BODY]: PICK_YOUR_GIFT_DEFAULT_BODY,
    [MSKU_PAGE_TITLE]: MSKU_PAGE_DEFAULT_TITLE,
    [MSKU_PAGE_BODY]: MSKU_PAGE_DEFAULT_BODY,
    [CUSTOMIZE_ITEMS_PAGE_TITLE]: CUSTOMIZE_ITEMS_PAGE_DEFAULT_TITLE,
    [CUSTOMIZE_ITEMS_PAGE_BODY]: CUSTOMIZE_ITEMS_PAGE_DEFAULT_BODY,
    [EMAIL_CUSTOM_SENDER]: EMAIL_CUSTOM_SENDER_DEFAULT,
  },
  [LOW_INVENTORY_NOTIFICATION_EMAILS]: [],
};

export const ORGANIZATIONS_FILTERS = [
  {
    label: 'All',
    tabId: OrganizationFilterTypesEnum.All,
  },
  {
    label: 'Activated',
    tabId: OrganizationFilterTypesEnum.Activated,
  },
  {
    label: 'Deactivated',
    tabId: OrganizationFilterTypesEnum.Deactivated,
  },
];

export const DEPARTMENT_TABLE_HEADERS = {
  [NAME]: 'Department name',
  [USED_BUDGET]: 'Budget',
  [AVAILABLE_BUDGET]: 'Limit',
};

export const EMAIL_LOGO_TYPES_OPTION_LABELS: { [key: string]: string } = {
  [EmailLogoTypesEnum.Rectangular]: 'Rectangular logo',
  [EmailLogoTypesEnum.Square]: 'Square logo',
};

export const OrganizationSuccessToastMessages = {
  [URL_VARS.NEW]: 'New organization was added',
  [URL_VARS.EDIT]: 'Organization was edited',
};

export const DEFAULT_SHIPPING_PAGE_CUSTOMIZATION_OPTIONS = (shippingPageUrl: string) => ({
  [DELAYED_SHIPPING_PAGE]: {
    [BUTTON_COLOR]: SHIPPING_PAGE_DEFAULT_BUTTON_COLOR,
    [HEADER_COLOR]: SHIPPING_PAGE_DEFAULT_HEADER_COLOR,
  },
  [DELAYED_SHIPPING_BASE_URL]: shippingPageUrl,
});

export const DEFAULT_EMAIL_CUSTOMIZATION_OPTIONS = {
  [ORG_CUSTOMIZATION_OPTIONS]: {
    [EMAIL_ACCENT_COLOR]: EMAIL_DEFAULT_ACCENT_COLOR,
    [EMAIL_LOGO_TYPE]: EMAIL_DEFAULT_LOGO_TYPE,
    [EMAIL_MAIN_COLOR]: EMAIL_DEFAULT_MAIN_COLOR,
    [EMAIL_USE_ORG_LOGO]: EMAIL_DEFAULT_USE_ORG_LOGO,
  } as IOrganizationCustomizationOptions,
};

export const DEFAULT_PORTAL_CUSTOMIZATION_OPTIONS = {
  [ORG_CUSTOMIZATION_OPTIONS]: {
    [PORTAL_ACCENT_COLOR]: THEME_DEFAULT_ACCENT_COLOR,
    [PORTAL_SIDEBAR_COLOR]: THEME_DEFAULT_SIDEBAR_COLOR,
    [PORTAL_SIDEBAR_TEXT_COLOR]: THEME_DEFAULT_SIDEBAR_TEXT_COLOR,
    [PORTAL_SIDEBAR_ACCENT_COLOR]: THEME_DEFAULT_SIDEBAR_ACCENT_COLOR,
    [PORTAL_USE_ORG_LOGO]: THEME_DEFAULT_USE_ORG_LOGO,
    [PORTAL_USE_COBRANDING_LOGO]: THEME_DEFAULT_USE_COBRANDING_ORG_LOGO,
  } as IOrganizationCustomizationOptions,
};
